import React, { FC, useCallback } from "react";
import { Container, Typography } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { RouteComponentProps, useLocation } from "react-router";
import { Helmet } from "react-helmet";
import { endOfTitle } from "../../../../constants/constants";
import { Form } from "react-final-form";
import Paper from "@material-ui/core/Paper";
import { NotifyMode } from "../../../notify/NotifyModeEnum";
import { mutateErrors } from "../../../../utils";
import { useRecaptcha } from "../../../../hooks/useRecaptcha";
import usePaymentQueries from "../../hooks/usePaymentQueries";
import { useNotify } from "../../../notify/useNotify";
import { IServicePayment } from "../../types";
import MakeServicePaymentForm from "../../components/MakeServicePaymentForm";
import { makeServicePaymentValidator } from "../../validators/makeServicePaymentValidator";
import queryString from "querystring";

const useStyles = makeStyles(() => ({
  container: {
    width: 800,
  },
  footer: {
    marginTop: 16,
  },
}));

const MakeServicePaymentPage: FC<RouteComponentProps> = () => {
  const classes = useStyles();

  const { executeRecaptcha } = useRecaptcha();
  const { createServiceCheckoutSession } = usePaymentQueries();
  const snackNotify = useNotify();
  const location = useLocation();

  const queryParams = queryString.parse(
    location.search.split("?").join(""),
    " ",
  );

  const serviceId = queryParams.serviceId as string;

  const handleSubmit = useCallback(
    async (data) => {
      try {
        const recaptchaToken = await executeRecaptcha(
          "create_service_checkout_session",
        );

        const servicePayment: IServicePayment = {
          serviceId: serviceId,
          amount: +data.amount,
          comment: data.comment,
        };

        const {
          data: { redirectUrl },
        } = await createServiceCheckoutSession(servicePayment, recaptchaToken);
        // redirecting to Stripe checkout page
        window.location.replace(redirectUrl);
      } catch (e) {
        const { message } = mutateErrors(e);
        snackNotify("Error", message, NotifyMode.ERROR);
      }
    },
    [createServiceCheckoutSession, executeRecaptcha, serviceId, snackNotify],
  );

  return (
    <Container maxWidth={"md"} className={classes.container}>
      <Helmet>
        <title>Make a Service Payment{endOfTitle}</title>
      </Helmet>
      <Paper>
        <Form
          onSubmit={handleSubmit}
          component={MakeServicePaymentForm}
          validate={makeServicePaymentValidator}
        />
        <Typography component="p" align="center" className={classes.footer}>
          This site is protected by reCAPTCHA and the Google{" "}
          <a href="https://policies.google.com/privacy">Privacy Policy</a> and{" "}
          <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
          apply.
        </Typography>
      </Paper>
    </Container>
  );
};

export default MakeServicePaymentPage;
